/**
 * Classifies sync failures as transient or permanent.
 *
 * The distinction decides whether a failed sync attempt burns an operation's
 * retry budget. A transient failure is one where the same write, unchanged,
 * would plausibly succeed later: the network is down, the auth token is stale,
 * rules were mid-deploy, Firestore was busy. Retrying is the correct response
 * and the attempt must not count toward quarantine — otherwise a few minutes
 * of bad connectivity is enough to push a receipt out of the pending queue.
 *
 * A permanent failure is one where retrying the identical payload cannot help:
 * the data is malformed, the target no longer exists, the operation type is
 * unknown. Those burn the budget and end in quarantine, where the user can
 * inspect and decide.
 *
 * permission-denied is neither, which is why there is a third disposition.
 * Firestore returns it for causes that clear in minutes — rules mid-deploy, a
 * custom claim that has not propagated to the token yet — and for one that
 * does not: a lapsed subscription, which persists until the user acts and is
 * the most common way a real write is refused. Treating it as transient means
 * those operations retry forever and never reach quarantine, so the user is
 * never told; treating it as permanent would quarantine work during a routine
 * rules deploy. The code cannot tell the causes apart, so time does: retry
 * freely inside a window, and start spending the budget once it closes.
 */

// Firestore/Firebase error codes worth retrying unchanged, indefinitely.
const TRANSIENT_CODES = new Set([
  'aborted', // transaction contention
  'cancelled',
  'deadline-exceeded',
  'internal',
  'resource-exhausted', // quota / rate limit
  'unauthenticated', // token expired, refresh pending
  'unavailable', // offline, backend unreachable
]);

// Codes that are transient only for as long as PERMISSION_RETRY_WINDOW_MS.
const BOUNDED_CODES = new Set(['permission-denied']);

/**
 * How long a rejected write keeps retrying for free.
 *
 * An hour comfortably covers both recoverable causes: a rules deploy reaches
 * every region in minutes, and a refreshed custom claim lands on the next
 * token refresh, which Firebase does hourly at the latest. Past that, a denial
 * is a standing state of the account. The budget is only starting to be spent
 * then — quarantine still takes MAX_SYNC_ATTEMPTS further failures — so the
 * real time to quarantine is this window plus however long those take.
 */
export const PERMISSION_RETRY_WINDOW_MS = 60 * 60 * 1000;

export type SyncErrorDisposition = 'transient' | 'bounded' | 'permanent';

// Substrings that identify a network-layer failure thrown as a plain Error
// rather than a FirebaseError (fetch rejections, aborted requests).
const TRANSIENT_MESSAGE_HINTS = [
  'failed to fetch',
  'network request failed',
  'networkerror',
  'load failed',
  'the operation was aborted',
  'err_internet_disconnected',
  'err_network_changed',
];

const codeOf = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as { code?: unknown }).code;
  if (typeof code !== 'string') return null;
  // FirebaseError codes are sometimes namespaced ('auth/network-request-failed')
  const bare = code.includes('/') ? code.slice(code.indexOf('/') + 1) : code;
  return bare.toLowerCase();
};

export const syncErrorDisposition = (error: unknown): SyncErrorDisposition => {
  const code = codeOf(error);
  if (code) {
    if (TRANSIENT_CODES.has(code)) return 'transient';
    if (BOUNDED_CODES.has(code)) return 'bounded';
    // 'auth/network-request-failed' and friends normalize to a hint match
    if (code.includes('network') || code.includes('timeout'))
      return 'transient';
    // A recognized non-transient Firebase code is definitive — do not fall
    // through to message sniffing, which could misread its description
    return 'permanent';
  }

  // Browsers report a dropped connection as a bare TypeError; treat the
  // navigator's own view of connectivity as corroborating evidence
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'transient';
  }

  const message = error instanceof Error ? error.message.toLowerCase() : '';
  return TRANSIENT_MESSAGE_HINTS.some(hint => message.includes(hint))
    ? 'transient'
    : 'permanent';
};

/**
 * True when the failure should not spend the retry budget at all.
 *
 * A bounded failure counts as transient until its window closes, so callers
 * pass the time of its first denial; without one it is treated as the first.
 */
export const isTransientSyncError = (
  error: unknown,
  firstDeniedAt?: number,
  now: number = Date.now()
): boolean => {
  const disposition = syncErrorDisposition(error);
  if (disposition === 'transient') return true;
  if (disposition === 'permanent') return false;
  return firstDeniedAt === undefined
    ? true
    : now - firstDeniedAt < PERMISSION_RETRY_WINDOW_MS;
};
